import { useState, useEffect } from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import {
  Sun,
  Moon,
  Monitor,
  Layout,
  Table2,
  Globe,
  Calendar,
  DollarSign,
  User,
  RotateCcw,
  ArrowLeft,
  ListTree,
  Columns,
  Palette,
  Check
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { rememberPageSize } from "@/hooks/usePreferredPageSize";
import { PAGE_SIZE_OPTIONS } from "@/components/shared/ERPPagination";

import {
  useUserPreferences,
  THEMES,
  UserPreferences,
} from "@/stores/useUserPreferences";

export default function AccountSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const prefs = useUserPreferences();
  const update = prefs.setPreference;
  const handleReset = prefs.resetPreferences;

  const initials = user?.fullName
    ? user.fullName
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "AD";

  const actions = (
    <Button data-testid="reset-preferences-btn" variant="outline" size="sm" onClick={() => {
      handleReset();
      toast.success("Đã khôi phục cài đặt mặc định (Menu Cây Dọc, Theme Xanh Lá Đậm Emerald)");
    }}>
      <RotateCcw size={14} className="mr-1.5" />
      Mặc định
    </Button>
  );

  return (
    <StandardDetailLayout
      title="Cài đặt người dùng"
      breadcrumbs={[
        { label: "Hệ thống", to: "/" },
        { label: "Cài đặt người dùng" },
      ]}
      actions={actions}
    >
      {/* 4 thẻ cài đặt: 1 cột mobile, 2 cột tablet, 4 cột màn rộng (bỏ khung 2 cột 1100px thừa khoảng trống) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start" data-testid="settings-grid">
          {/* Profile Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <User size={16} />
                Hồ sơ cá nhân
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative group shrink-0">
                  <div
                    className="h-16 w-16 rounded-full flex items-center justify-center text-lg font-bold text-white shadow-md transition-colors"
                    style={{
                      backgroundColor:
                        THEMES.find((t) => t.value === prefs.themeName)
                          ?.color || prefs.accentColor,
                    }}
                  >
                    {initials}
                  </div>
                </div>
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Họ tên</Label>
                      <Input
                        value={user?.fullName || "—"}
                        disabled
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Email</Label>
                      <Input
                        value={user?.email || "—"}
                        disabled
                        className="h-9"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="text-[10px]"
                      style={{
                        backgroundColor: `color-mix(in srgb, ${THEMES.find((t) => t.value === prefs.themeName)?.color || prefs.accentColor} 15%, white)`,
                        color:
                          THEMES.find((t) => t.value === prefs.themeName)
                            ?.color || prefs.accentColor,
                      }}
                    >
                      {user?.role || "user"}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Theme & Colors */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Palette size={16} />
                Giao diện & Màu sắc
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Theme mode */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm">Chế độ hiển thị</Label>
                <div className="flex gap-1.5">
                  {(
                    [
                      ["light", Sun, "Sáng"],
                      ["dark", Moon, "Tối"],
                      ["auto", Monitor, "Tự động"],
                    ] as const
                  ).map(([val, Icon, lbl]) => (
                    <button
                      key={val}
                      onClick={() => update("themeMode", val)}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all",
                        prefs.themeMode === val
                          ? "font-medium border"
                          : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-transparent",
                      )}
                      style={
                        prefs.themeMode === val
                          ? {
                              background: prefs.accentColor || "#2563eb",
                              color: "white",
                              borderColor: prefs.accentColor || "#2563eb",
                            }
                          : undefined
                      }
                    >
                      <Icon size={14} />
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent color */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Label className="text-sm">Màu chủ đạo (Theme Style)</Label>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Mặc định: Emerald (Xanh lá đậm APV)
                    </p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-md" style={{ color: prefs.accentColor }}>
                    {THEMES.find((t) => t.value === prefs.themeName)?.label || "Emerald"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2.5 pt-1">
                  {THEMES.map((t) => {
                    const isSelected = prefs.themeName === t.value;
                    return (
                      <button
                        key={t.value}
                        onClick={() => {
                          update("themeName", t.value);
                          update("accentColor", t.color);
                          toast.success(`Đã đổi màu chủ đạo: ${t.label}`);
                        }}
                        className={cn(
                          "w-8 h-8 rounded-full transition-all border relative flex items-center justify-center shadow-xs",
                          isSelected
                            ? "ring-2 ring-offset-2 ring-blue-500 scale-110"
                            : "hover:scale-105 border-black/10",
                        )}
                        style={{ backgroundColor: t.color }}
                        title={t.label}
                      >
                        {isSelected && (
                          <Check size={14} className="text-white drop-shadow-xs stroke-[3]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Layout & Navigation Mode */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Layout size={16} />
                Bố cục & Điều hướng Menu
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Menu Layout Picker */}
              <div className="space-y-2">
                <div>
                  <Label className="text-sm font-semibold">Kiểu hiển thị Menu</Label>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Tùy chỉnh kiểu bố cục menu điều hướng phân hệ và chức năng con
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 pt-1">
                  {/* Option 1: Dual-Axis */}
                  <div
                    onClick={() => {
                      update("menuLayout", "dual-axis");
                      toast.success("Đã kích hoạt: Kiểu Menu Ngang kết hợp Dọc (Dual-Axis)");
                    }}
                    className={cn(
                      "cursor-pointer rounded-xl p-3 border-2 transition-all text-left flex flex-col justify-between space-y-2 relative group",
                      prefs.menuLayout === "dual-axis"
                        ? "border-blue-600 bg-blue-50/50 shadow-xs"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-md bg-blue-600 text-white shadow-xs">
                          <Columns size={16} />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">
                            Ngang & Dọc
                          </p>
                          <span className="text-[10px] text-blue-600 font-semibold">
                            Dual-Axis
                          </span>
                        </div>
                      </div>
                      {prefs.menuLayout === "dual-axis" && (
                        <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0">Đang dùng</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Phân hệ ở Sidebar trái + chức năng con dạng thanh tab ngang dưới Header. Tối ưu không gian bảng dữ liệu.
                    </p>
                  </div>

                  {/* Option 2: TreeView */}
                  <div
                    onClick={() => {
                      update("menuLayout", "treeview");
                      toast.success("Đã kích hoạt: Kiểu Menu Cây Dọc (TreeView Accordion)");
                    }}
                    className={cn(
                      "cursor-pointer rounded-xl p-3 border-2 transition-all text-left flex flex-col justify-between space-y-2 relative group",
                      (prefs.menuLayout || "treeview") === "treeview"
                        ? "border-blue-600 bg-blue-50/50 shadow-xs"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-md bg-blue-600 text-white shadow-xs">
                          <ListTree size={16} />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">
                            Cây phân cấp Dọc
                          </p>
                          <span className="text-[10px] text-blue-600 font-semibold">
                            TreeView Sidebar (Mặc định)
                          </span>
                        </div>
                      </div>
                      {(prefs.menuLayout || "treeview") === "treeview" && (
                        <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0">Đang dùng</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Toàn bộ phân hệ và chức năng con hiển thị trực tiếp trong Sidebar dạng cây Accordion đóng/mở.
                    </p>
                  </div>
                </div>
              </div>

              {/* Display Density */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div>
                  <Label className="text-sm">Mật độ hiển thị</Label>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Khoảng cách giữa các phần tử
                  </p>
                </div>
                <div className="flex gap-1.5">
                  {(
                    [
                      ["comfortable", "Thoải mái"],
                      ["compact", "Thu gọn"],
                    ] as const
                  ).map(([val, lbl]) => (
                    <button
                      key={val}
                      onClick={() => update("density", val)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-sm transition-all",
                        prefs.density === val
                          ? "font-medium border"
                          : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-transparent",
                      )}
                      style={
                        prefs.density === val
                          ? {
                              background: prefs.accentColor || "#2563eb",
                              color: "white",
                              borderColor: prefs.accentColor || "#2563eb",
                            }
                          : undefined
                      }
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sidebar collapse mode */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label className="text-sm">Sidebar mặc định</Label>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Trạng thái menu bên trái khi mở
                  </p>
                </div>
                <Select
                  value={prefs.sidebarMode}
                  onValueChange={(v) =>
                    update("sidebarMode", v as "auto" | "full" | "collapsed")
                  }
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Mở rộng</SelectItem>
                    <SelectItem value="collapsed">Thu nhỏ (icon)</SelectItem>
                    <SelectItem value="auto">Tự động</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Welcome card switch */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label className="text-sm">Hiện lời chào</Label>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Hiển thị Dashboard welcome card
                  </p>
                </div>
                <Switch
                  checked={prefs.showWelcome}
                  onCheckedChange={(v) => update("showWelcome", v)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Data Display */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Table2 size={16} />
                Hiển thị dữ liệu
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm flex items-center gap-1.5">
                  <Table2 size={14} />
                  Số dòng/trang
                </Label>
                <p className="text-xs text-gray-400 mt-0.5 hidden sm:block">
                  Mặc định cho tất cả bảng danh sách
                </p>
                <Select
                  value={String(prefs.tablePageSize)}
                  // Áp dụng cho mọi danh sách; đổi ở ô số dòng dưới danh sách cũng cập nhật ô này
                  onValueChange={(v) => rememberPageSize(Number(v))}
                >
                  <SelectTrigger className="w-24" data-testid="settings-table-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm flex items-center gap-1.5">
                  <Calendar size={14} />
                  Định dạng ngày
                </Label>
                <Select
                  value={prefs.dateFormat}
                  onValueChange={(v) => update("dateFormat", v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DD/MM/YYYY">dd/MM/yyyy (VN)</SelectItem>
                    <SelectItem value="YYYY-MM-DD">yyyy-MM-dd (ISO)</SelectItem>
                    <SelectItem value="MM/DD/YYYY">MM/dd/yyyy (US)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm flex items-center gap-1.5">
                  <DollarSign size={14} />
                  Hiển thị tiền tệ
                </Label>
                <Select
                  value={prefs.currencyDisplay}
                  onValueChange={(v) => update("currencyDisplay", v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="symbol">Ký hiệu (₫)</SelectItem>
                    <SelectItem value="code">Mã (VND)</SelectItem>
                    <SelectItem value="name">Tên đầy đủ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm flex items-center gap-1.5">
                  <Globe size={14} />
                  Ngôn ngữ
                </Label>
                <Select
                  value={prefs.language}
                  onValueChange={(v) => update("language", v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vi">Tiếng Việt</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
      </div>
    </StandardDetailLayout>
  );
}
